import { Button } from "@/components/design-system/button";
import {
  formatInr,
  getCourseTax,
  getCourseTotal,
  getGstLabel,
} from "../utils/landingPricing";
import type { LandingCourse } from "../types/landing.types";
import {
  LANDING_CARD_MAX_WIDTH,
  LANDING_ORDER_SUMMARY_TYPOGRAPHY,
} from "../constants/landing.constants";
import LandingIcon from "./LandingIcon";

interface OrderSummaryCardProps {
  course: LandingCourse;
  showEnrollButton?: boolean;
  onEnroll?: () => void;
  className?: string;
}

export default function OrderSummaryCard({
  course,
  showEnrollButton = false,
  onEnroll,
  className = "",
}: OrderSummaryCardProps) {
  const tax = getCourseTax(course);
  const total = getCourseTotal(course);

  return (
    <div
      className={`w-full overflow-hidden rounded-[16px] border border-landing-border bg-white shadow-landing-card ${LANDING_CARD_MAX_WIDTH} ${className}`}
    >
      <div className="relative">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="h-[200px] w-full object-cover"
        />
        {(course.audienceLabel ?? course.ageLabel) ? (
          <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.overlayBadge}>
            {course.audienceLabel ?? course.ageLabel}
          </span>
        ) : null}
      </div>

      <div className="p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.headerLabel}>
            Class Details
          </span>
          <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.durationBadge}>
            <LandingIcon
              name="checkCircle"
              className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.durationIcon}
            />
            {course.durationDays} Days
          </span>
        </div>

        <div className="space-y-3">
          <div className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.priceRow}>
            <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.priceLabel}>Price</span>
            <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.priceValue}>
              {formatInr(course.price)}
            </span>
          </div>
          <div className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.priceRow}>
            <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.priceLabel}>
              {getGstLabel(course)}
            </span>
            <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.priceValue}>
              {formatInr(tax)}
            </span>
          </div>
          <hr className="border-landing-border" />
          <div className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.totalRow}>
            <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.totalLabel}>
              Total Price
            </span>
            <span className={LANDING_ORDER_SUMMARY_TYPOGRAPHY.totalAmount}>
              {formatInr(total)}
            </span>
          </div>
        </div>

        {showEnrollButton ? (
          <Button
            type="button"
            variant="primary"
            className="mt-5 hidden h-[48px] w-full rounded-[12px] bg-landing-button text-[16px] font-semibold hover:bg-landing-button-hover md:inline-flex"
            onClick={onEnroll}
          >
            Enroll Now
          </Button>
        ) : null}
      </div>
    </div>
  );
}
